/* eslint-disable no-nested-ternary */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useLiterals from 'utils/hooks/useLiterals';
import Card from 'components/UI/Card';
import { ROUTE_PATH } from 'routes';
import SETTINGS_SECTIONS from 'constants/settings';
import { STORAGE_PUSH_NOTIFICATIONS, STORAGE_USER_DATA } from 'constants/local';
import useLocalStorage from 'utils/hooks/useLocalStorage';
import { useBiometricAvailable } from 'utils/hooks/useBiometricAvailable';
import Button from 'components/UI/Button';
import useFeatureGate from 'utils/hooks/useFeatureGate';
import { FEATURE_BIOMETRIC_SETUP, FEATURE_PUSH_NOTIFICATIONS } from 'constants/featureGates';
import platform from 'utils/platform';

const Settings = () => {
  const literals = useLiterals('settings');
  const { user } = useSelector((state) => state.session);
  const [userData] = useLocalStorage(STORAGE_USER_DATA);
  const [notifications] = useLocalStorage(STORAGE_PUSH_NOTIFICATIONS);
  const biometricAvailable = useBiometricAvailable();
  const biometricSetupEnabled = useFeatureGate(FEATURE_BIOMETRIC_SETUP);
  const pushNotificationsEnabled = useFeatureGate(FEATURE_PUSH_NOTIFICATIONS);
  const [showLog, setShowLog] = useState(0);
  const [logs, setLogs] = useState([]);
  const navigate = useNavigate();

  const nativeVersion = useMemo(() => {
    if (platform.isNativeApp && window?.mlsQueryParams?.v) {
      return window.mlsQueryParams.v;
    }
    return '';
  }, []);

  const handleGoToBiometricAccessSettings = () => {
    navigate(ROUTE_PATH.setSettings(SETTINGS_SECTIONS.BIOMETRIC));
  };

  const handleGoToModifyMlsPasswordSettings = () => {
    navigate(ROUTE_PATH.setSettings(SETTINGS_SECTIONS.MODIFY_PASSWORD));
  };

  const handleGoToPushNotificationsSettings = () => {
    navigate(ROUTE_PATH.setSettings(SETTINGS_SECTIONS.PUSH_NOTIFICATIONS));
  };

  const handleGoToSAPAssociateSettings = () => {
    navigate(ROUTE_PATH.setSettings(SETTINGS_SECTIONS.SAP_ASSOCIATE));
  };

  const handleGetGeolocation = () => {
    if (navigator.geolocation) {
      setLogs((prev) => [...prev, 'Geolocation Start']);
      navigator.geolocation.getCurrentPosition((response) => {
        const crd = response.coords;
        setLogs((prev) => [...prev, `Latitude : ${crd.latitude} - Longitude: ${crd.longitude} - Accuracy: ${crd.accuracy} meters.`]);
      }, (error) => {
        setLogs((prev) => [...prev, `Geolocation Error: ${error}`]);
      });
    }
  };

  return (
    <>
      <Card
        title={literals.language}
        description={literals.spanish}
      />
      {biometricSetupEnabled && biometricAvailable !== null && user?.id && (
        <Card
          title={literals.biometricAccess}
          description={biometricAvailable ? (userData?.biometric ? literals.enabled : literals.disabled) : literals.unavailable}
          onClick={handleGoToBiometricAccessSettings}
          showAccessIcon={biometricAvailable}
        />
      )}
      {pushNotificationsEnabled && (
        <Card
          title={literals.pushNotifications}
          description={notifications.enabled ? literals.enabled : literals.disabled}
          onClick={handleGoToPushNotificationsSettings}
          showAccessIcon
        />
      )}
      {user?.id ? (
        <>
          <Card
            title={literals.modifyMlsPassword}
            onClick={handleGoToModifyMlsPasswordSettings}
            showAccessIcon
          />
          <Card
            title={literals.sapAssociate}
            description={user?.clientId ? user.clientId : literals.notAssociated}
            onClick={handleGoToSAPAssociateSettings}
            showAccessIcon
          />
        </>
      ) : null}
      <Card
        title={literals.appVersion}
        description={`${nativeVersion} / ${window.REACT_APP_BUILD_VERSION}${window.ENVIROMENT !== 'pro' ? ` (${window.ENVIROMENT})` : ''}`}
        onClick={() => setShowLog((prev) => prev + 1)}
      />
      {showLog >= 10 ? (
        <div>
          <div className='text-neutral-500 mt-6'>
            <p className='mb-2 font-semibold text-sm'>{literals.queryParams}</p>
            {Object.keys(window.mlsQueryParams || {}).map((key) => (
              <p className='mb-1 text-sm' key={key}>{`${key}: ${window.mlsQueryParams[key]}`}</p>
            ))}
            <p className='mb-2 mt-2 font-semibold text-sm'>{literals.logs}</p>
            <div className='settings-logs'>
              {logs.map((t, index) => (
                <div key={index}>
                  <hr />
                  <p className='text-sm m-0' dangerouslySetInnerHTML={{ __html: t }} />
                </div>
              ))}
            </div>
          </div>
          <div className='mt-6'>
            <div className='flex gap-4 mb-6'>
              <Button to={ROUTE_PATH.setKlu(true)} type='primary' text='KLU Login' />
              <Button to={ROUTE_PATH.setKlu()} type='primary' text='KLU Signup' />
            </div>
            <Button className='mb-2' type='secondary' text='Geolocation' onClick={handleGetGeolocation} />
            <iframe title='Geolocation text' width='100%' height={250} allow='geolocation; camera; microphone; accelerometer; gyroscope' src='https://webintra.net/app/632fbbd66edc6e90b9958e641d4d8ffdbf689e558629aa0489dfa7a62d57887785f6f9b342109faded84706076f8c3b4f5ab78b9770597e98cceede827e4a6f99c5fe96aa2361757490577/' />
          </div>
        </div>
      ) : null}
    </>
  );
};

export default Settings;
